import {
  DSButton,
  DSInput,
} from "@components/design-system/molecules";
import {
  faEye,
  faEyeSlash,
} from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth } from "@hooks/use-auth";
import { config, useTransition, animated } from "@react-spring/web";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useState } from "react";
import { Link } from "react-router";

import { useRedirectAuthenticatedUsers } from "./use-redirect-authenticated-users";
import OttoScribblesPeek from "../../assets/imgs/otto-scribbles-peek.png";

function Login() {
  const [email, setEmail] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [shouldShowOttoScribbles, setShouldShowOttoScribbles] =
    useState(true);

  const auth = useAuth();

  const { descopeAuth, descopeEmailDomains } = useFlags();

  const isDescopeLogin = (() => {
    if (!descopeAuth) return false;

    for (const descopeEmailDomain of descopeEmailDomains || []) {
      if (email.endsWith(`@${descopeEmailDomain}`)) {
        return true;
      }
    }

    return false;
  })();

  useRedirectAuthenticatedUsers();

  function togglePassword() {
    setShowPassword(!showPassword);
  }
  const popUpAnim = useTransition(shouldShowOttoScribbles, {
    from: { y: 0 },
    enter: { y: 0 },
    leave: { y: 40 },
    delay: 200,
    config: config.gentle,
  });

  const handleLogin = async (event) => {
    event.preventDefault();
    event.persist(); // <- Prevent pooling from clearing form during setstate

    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      return;
    }

    const { email, password } = event.target;
    if (isDescopeLogin) {
      await auth.descopeSignin(email.value);
    } else {
      await auth.signin(email.value, password.value);
    }
  };

  return (
    <div className="h-full flex flex-col items-center justify-center space-y-10 pb-10">
      <div>
        <h2 className="mb-10 text-center text-3xl font-extrabold text-primary">
          Sign in to your account
        </h2>
      </div>

      <div className="flex justify-center relative">
        {popUpAnim(
          (styles, item) =>
            item && (
              <animated.div
                style={styles}
                className="absolute z-0 -top-10"
              >
                <img src={OttoScribblesPeek} width="200px" />
              </animated.div>
            ),
        )}
        <form
          className="flex flex-col justify-center w-[300px] md:w-[400px] mx-auto z-10 p-10 rounded-xl shadow-md shadow-secondary bg-secondary"
          data-testid="loginForm"
          onSubmit={(event) => handleLogin(event)}
        >
          <div className="flex flex-col space-y-10 w-full">
            <DSInput
              name="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              dataTestId="email"
              placeholder="Email Address"
            />
            {!isDescopeLogin && (
              <div className="relative">
                <DSInput
                  name="password"
                  required
                  dataTestId="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  onFocus={() => setShouldShowOttoScribbles(false)}
                  onBlur={() => setShouldShowOttoScribbles(true)}
                />
                <button
                  type="button"
                  onClick={() => togglePassword()}
                  className="group absolute -top-5 right-2 z-20 flex items-center space-x-1 text-secondary"
                >
                  <FontAwesomeIcon
                    icon={showPassword ? faEyeSlash : faEye}
                    size="sm"
                    className="group-hover:text-brand"
                  />
                  <h5 className="text-xs group-hover:text-brand">
                    {showPassword ? "Hide" : "Show"}
                  </h5>
                </button>
              </div>
            )}
          </div>

          {!isDescopeLogin && (
            <div className="self-end">
              <Link
                to="/forgot_password"
                className="font-medium text-brand text-xs"
              >
                Forgot your password?
              </Link>
            </div>
          )}
          <div className="pt-5 flex justify-center">
            <DSButton
              dataTestId="login"
              type="submit"
              variant="primary"
              size="lg"
            >
              Sign In
            </DSButton>
          </div>
        </form>
      </div>
      <p
        className="mt-2 text-center text-sm dark:text-gray-400 text-gray-600"
        data-testid="registerFromLogin"
      >
        Or{" "}
        <Link to="/auth/register" className="font-medium text-brand">
          register a new account
        </Link>
      </p>
    </div>
  );
}

export default Login;
